(() => {
  class BhNav extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;

      this.tabs = Array.from(this.querySelectorAll('[data-bh-department-trigger]'));
      this.departments = Array.from(this.querySelectorAll('[data-bh-department-panel]'));
      this.families = Array.from(this.querySelectorAll('[data-bh-family-trigger]'));
      this.megas = Array.from(this.querySelectorAll('[data-bh-mega]'));
      this.groupToggles = Array.from(this.querySelectorAll('[data-bh-group-toggle]'));
      this.backToDepartmentsButtons = Array.from(this.querySelectorAll('[data-bh-back-departments]'));
      this.backToFamiliesButtons = Array.from(this.querySelectorAll('[data-bh-back-families]'));
      this.currentDepartment = this.dataset.bhDefaultDepartment || this.tabs[0]?.dataset.bhDepartmentTrigger;
      this.currentFamily = null;
      this.suppressFocusOpen = null;
      this.pendingEscapeKeyup = false;
      this.mobileMode = null;
      this.lastScrollTop = window.scrollY;
      this.mainDisclosure = this.closest('.main-menu__disclosure');
      this.headerElement = this.closest('store-header');

      this.tabs.forEach((tab) => {
        tab.addEventListener('mouseenter', () => this.selectDepartment(tab.dataset.bhDepartmentTrigger));
        tab.addEventListener('focus', () => this.selectDepartment(tab.dataset.bhDepartmentTrigger));
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          this.selectDepartment(tab.dataset.bhDepartmentTrigger);
          if (!this.isMobile()) return;
          this.setMobileLevel('families');
          this.scrollMobilePanelTop();
        });
        tab.addEventListener('keydown', (event) => this.handleTabKey(event, tab));
      });

      this.families.forEach((family) => {
        const open = () => this.openFamily(family);
        family.addEventListener('mouseenter', open);
        family.addEventListener('focus', () => {
          if (this.suppressFocusOpen === family) {
            this.suppressFocusOpen = null;
            return;
          }
          if (!this.isMobile()) open();
        });
        family.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.openFamily(family, true);
        });
        family.addEventListener('click', (event) => {
          const isKeyboardActivation = event.detail === 0;
          event.preventDefault();
          this.openFamily(family, isKeyboardActivation);
        });
      });

      this.groupToggles.forEach((toggle) => {
        if (toggle.dataset.bhGroupBound === 'true') return;
        toggle.dataset.bhGroupBound = 'true';
        toggle.dataset.bhGroupOpen = 'false';
        toggle.addEventListener('click', (event) => {
          event.stopImmediatePropagation();
          const shouldOpen = toggle.dataset.bhGroupOpen !== 'true';
          this.toggleGroup(toggle, shouldOpen);
        }, true);
      });
      this.backToDepartmentsButtons.forEach((button) => {
        button.addEventListener('click', () => this.backToDepartments());
      });
      this.backToFamiliesButtons.forEach((button) => {
        button.addEventListener('click', () => this.backToFamilies());
      });

      this.addEventListener('mouseleave', () => {
        if (!this.isMobile()) this.closeMegas();
      });
      this.scrollHandler = () => {
        const nextScrollTop = window.scrollY;
        if (!this.isMobile() && nextScrollTop > this.lastScrollTop + 4) this.closeMegas();
        this.lastScrollTop = nextScrollTop;
      };
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      this.escapeHandler = (event) => {
        if (event.key === 'Escape') {
          if (this.isMobile() && this.dataset.bhMobileLevel === 'mega') {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.pendingEscapeKeyup = true;
            this.backToFamilies();
            return;
          }
          if (this.isMobile() && this.dataset.bhMobileLevel === 'families') {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.pendingEscapeKeyup = true;
            this.backToDepartments();
            return;
          }
          const trigger = this.currentFamily;
          if (!trigger) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.pendingEscapeKeyup = true;
          this.closeMegas();
          if (document.activeElement === trigger) {
            this.suppressFocusOpen = null;
          } else {
            this.suppressFocusOpen = trigger;
            trigger.focus();
          }
        }
      };
      this.escapeKeyupHandler = (event) => {
        if (event.key !== 'Escape' || !this.pendingEscapeKeyup) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.pendingEscapeKeyup = false;
      };
      document.addEventListener('keydown', this.escapeHandler, true);
      this.addEventListener('keyup', this.escapeKeyupHandler, true);

      this.syncResponsiveState = () => {
        if (!this.mainDisclosure) return;
        const isDesktop = !this.isMobile();
        this.mainDisclosure.open = isDesktop || this.mainDisclosure.open;
        this.mainDisclosure.classList.toggle('is-open', isDesktop || this.mainDisclosure.open);
        if (this.mobileMode === !isDesktop) return;

        this.mobileMode = !isDesktop;
        if (this.mobileMode) {
          this.closeMegas();
          this.setMobileLevel('departments');
          this.syncGroupDisclosure(true);
        } else {
          this.syncGroupDisclosure(false);
        }
      };
      this.disclosureToggleHandler = () => {
        if (this.isMobile() && !this.mainDisclosure?.open) {
          this.closeMegas();
          this.setMobileLevel('departments');
          this.syncGroupDisclosure(true);
        }
      };
      this.mainDisclosure?.addEventListener('toggle', this.disclosureToggleHandler);
      this.syncResponsiveState();
      window.addEventListener('resize', this.syncResponsiveState);

      this.initialiseState();
      this.dataset.bhNavReady = 'true';
      this.dataset.bound = 'true';
    }

    disconnectedCallback() {
      if (this.escapeHandler) document.removeEventListener('keydown', this.escapeHandler, true);
      if (this.escapeKeyupHandler) this.removeEventListener('keyup', this.escapeKeyupHandler, true);
      if (this.syncResponsiveState) window.removeEventListener('resize', this.syncResponsiveState);
      if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
      if (this.disclosureToggleHandler) this.mainDisclosure?.removeEventListener('toggle', this.disclosureToggleHandler);
      this.setMenuOpenState(false);
    }

    initialiseState() {
      const selected = this.currentDepartment || this.tabs[0]?.dataset.bhDepartmentTrigger;
      if (!selected) return;

      this.selectDepartment(selected);
      this.closeMegas();
    }

    isMobile() {
      return window.matchMedia('(max-width: 768px)').matches;
    }

    setMobileLevel(level) {
      this.dataset.bhMobileLevel = level;
    }

    scrollMobilePanelTop() {
      this.scrollTop = 0;
      const scrollContainer = this.closest('.main-menu__content');
      if (scrollContainer) scrollContainer.scrollTop = 0;
    }

    syncGroupDisclosure(isMobile) {
      this.groupToggles.forEach((toggle) => {
        const contentId = toggle.getAttribute('aria-controls');
        const content = contentId ? this.querySelector(`#${CSS.escape(contentId)}`) : null;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.dataset.bhGroupOpen = 'false';
        if (content) content.hidden = isMobile;
      });
    }

    toggleGroup(toggle, requestedOpen) {
      if (!this.isMobile()) return;

      const targetContentId = toggle.getAttribute('aria-controls');
      const targetContent = targetContentId ? this.querySelector(`#${CSS.escape(targetContentId)}`) : null;
      const shouldOpen = typeof requestedOpen === 'boolean' ? requestedOpen : targetContent?.hidden !== false;
      this.groupToggles.forEach((candidate) => {
        const contentId = candidate.getAttribute('aria-controls');
        const content = contentId ? this.querySelector(`#${CSS.escape(contentId)}`) : null;
        const open = candidate === toggle && shouldOpen;
        candidate.setAttribute('aria-expanded', String(open));
        candidate.dataset.bhGroupOpen = String(open);
        if (content) content.hidden = !open;
      });
    }

    backToDepartments() {
      if (!this.isMobile()) return;
      this.closeMegas();
      this.setMobileLevel('departments');
      this.scrollMobilePanelTop();
      this.tabs.find((tab) => tab.dataset.bhDepartmentTrigger === this.currentDepartment)?.focus();
    }

    backToFamilies() {
      if (!this.isMobile()) return;
      const trigger = this.currentFamily;
      this.closeMegas();
      this.syncGroupDisclosure(true);
      this.setMobileLevel('families');
      this.scrollMobilePanelTop();
      trigger?.focus();
    }

    selectDepartment(departmentId) {
      if (!departmentId) return;
      this.currentDepartment = departmentId;

      this.tabs.forEach((tab) => {
        const selected = tab.dataset.bhDepartmentTrigger === departmentId;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
      });

      this.departments.forEach((department) => {
        department.hidden = department.dataset.bhDepartmentPanel !== departmentId;
        department.classList.toggle('is-active', !department.hidden);
      });

      this.closeMegas();
    }

    openFamily(trigger, focusPanel = false) {
      const familyId = trigger.dataset.bhFamilyTrigger;
      const mega = this.megas.find((candidate) => candidate.dataset.bhMega === familyId);
      if (!mega) return;

      const departmentId = mega.dataset.bhFamilyOwner;
      if (departmentId !== this.currentDepartment) this.selectDepartment(departmentId);

      this.megas.forEach((candidate) => {
        const open = candidate === mega;
        candidate.hidden = !open;
        candidate.classList.toggle('is-open', open);
      });
      this.families.forEach((candidate) => {
        const expanded = candidate === trigger;
        candidate.setAttribute('aria-expanded', String(expanded));
      });
      this.currentFamily = trigger;
      if (this.isMobile()) {
        this.syncGroupDisclosure(true);
        this.setMobileLevel('mega');
        this.scrollMobilePanelTop();
      }
      this.setMenuOpenState(true);
      if (focusPanel) this.focusFirstPanelElement(mega);
    }

    focusFirstPanelElement(panel) {
      const candidates = panel.querySelectorAll(
        '.bh-nav__view-all, .bh-nav__links a, .bh-nav__group-toggle, a[href], button:not([disabled])',
      );
      const target = Array.from(candidates).find((candidate) => {
        if (candidate.hidden || candidate.closest('[hidden]')) return false;
        if (typeof candidate.getClientRects !== 'function') return true;
        return candidate.getClientRects().length > 0;
      });
      target?.focus();
    }

    closeMegas() {
      this.megas.forEach((mega) => {
        mega.hidden = true;
        mega.classList.remove('is-open');
      });
      this.families.forEach((family) => family.setAttribute('aria-expanded', 'false'));
      this.currentFamily = null;
      this.setMenuOpenState(false);
    }

    setMenuOpenState(isOpen) {
      this.headerElement?.classList.toggle('bh-nav-menu-open', isOpen);
    }

    handleTabKey(event, tab) {
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();

      let index = this.tabs.indexOf(tab);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.tabs.length - 1;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') index = (index + 1) % this.tabs.length;
      else index = (index - 1 + this.tabs.length) % this.tabs.length;

      const next = this.tabs[index];
      next?.focus();
      if (next) this.selectDepartment(next.dataset.bhDepartmentTrigger);
    }
  }

  if (!customElements.get('bh-nav')) customElements.define('bh-nav', BhNav);
})();
